import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Popover } from "radix-ui";
import type { AuditEntry, CommunityStructure, Member, Page, UserSummary } from "@atrium/shared";
import { api, errorMessage, qs } from "../../../lib/api";
import { cn } from "../../../lib/cn";
import { chatTimestamp, timeAgo } from "../../../lib/format";
import { useInView } from "../../../lib/hooks";
import { Avatar } from "../../../components/ui/Avatar";
import { Button, IconButton } from "../../../components/ui/Button";
import { EmptyState } from "../../../components/ui/Controls";
import { Input } from "../../../components/ui/Input";
import { Select, SettingsGroup } from "../../../components/ui/Settings";
import { RowSkeleton } from "../../../components/ui/Skeleton";
import { Spinner } from "../../../components/ui/Spinner";
import { toast } from "../../../components/ui/Toast";
import { Tooltip } from "../../../components/ui/Tooltip";
import {
  IconCopy,
  IconCrown,
  IconHistory,
  IconSearch,
  IconTimer,
  IconTrash,
  IconUserPlus,
} from "../../../components/icons";
import { copyText } from "../../posts/usePostActions";
import { NameTags } from "../../../components/UserBadges";
import { permissionsIn } from "../../../lib/permissions";
import { MemberCard, memberStatus } from "../MemberList";
import { useMembers } from "../hooks";
import { InviteDialog } from "../InviteDialog";
import type { SectionProps } from "./shared";

/* ───────────── Members ───────────── */

export function MembersSection({ structure }: SectionProps) {
  const { data, isPending } = useMembers(structure.community.id);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [sort, setSort] = useState<"joined" | "name">("joined");
  const members = useMemo(() => {
    const list = (data?.pages.flatMap((p) => p.items) ?? [])
      .filter((m) => !q || `${m.user.displayName} ${m.user.username}`.toLowerCase().includes(q.toLowerCase()))
      .filter((m) => !role || (role === "timeout" ? !!m.timeoutUntil : m.roleIds.includes(role)));
    return list.sort((a, b) => (sort === "name" ? a.user.displayName.localeCompare(b.user.displayName) : b.joinedAt.localeCompare(a.joinedAt)));
  }, [data, q, role, sort]);
  const roles = structure.roles.filter((r) => !r.isDefault);

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-48 flex-1">
          <IconSearch className="icon pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" aria-label="Search members" className="!h-9 pl-8" />
        </div>
        <Select
          aria-label="Filter by role"
          value={role}
          onChange={setRole}
          className="[&_select]:h-9"
          options={[{ value: "", label: "All roles" }, ...roles.map((r) => ({ value: r.id, label: r.name })), { value: "timeout", label: "Timed out" }]}
        />
        <Select
          aria-label="Sort"
          value={sort}
          onChange={setSort}
          className="[&_select]:h-9"
          options={[
            { value: "joined", label: "Newest first" },
            { value: "name", label: "Name" },
          ]}
        />
      </div>
      <SettingsGroup title={isPending ? undefined : `${members.length} ${members.length === 1 ? "member" : "members"}`}>
        {isPending ? Array.from({ length: 4 }, (_, i) => <RowSkeleton key={i} />) : members.map((m) => <MemberLine key={m.user.id} member={m} structure={structure} />)}
        {!isPending && members.length === 0 && <p className="px-4 py-3 text-sm text-fg-3">No members match.</p>}
      </SettingsGroup>
    </div>
  );
}

function MemberLine({ member, structure }: { member: Member; structure: CommunityStructure }) {
  const [open, setOpen] = useState(false);
  const roles = structure.roles.filter((r) => member.roleIds.includes(r.id));
  const timedOut = member.timeoutUntil && new Date(member.timeoutUntil) > new Date();
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <Avatar src={member.user.avatarUrl} name={member.user.displayName} size="md" status={memberStatus(member)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <span className="truncate" style={{ color: roles.find((r) => r.color)?.color ?? undefined }}>
            {member.user.displayName}
          </span>
          <NameTags user={member.user} />
          {member.isOwner && <IconCrown className="icon size-3.5 text-warning" aria-label="Owner" />}
          {timedOut && (
            <Tooltip label={`Timed out until ${chatTimestamp(member.timeoutUntil!)}`}>
              <IconTimer className="icon size-3.5 text-danger" aria-label="Timed out" />
            </Tooltip>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1 text-xs text-fg-3">
          <span>@{member.user.username}</span>
          <span>· joined {timeAgo(member.joinedAt)}</span>
          {roles.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1 rounded bg-surface px-1.5 py-px text-fg-2">
              <span className="size-1.5 rounded-full" style={{ background: r.color ?? "var(--fg-3)" }} />
              {r.name}
            </span>
          ))}
        </div>
      </div>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <Button size="sm" variant="outline">
            Manage
          </Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={6} collisionPadding={8} className="z-[60] rounded-lg bg-surface shadow-pop animate-pop-in">
            <MemberCard member={member} structure={structure} onClose={() => setOpen(false)} />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

/* ───────────── Invites ───────────── */

export function InvitesSection({ structure }: SectionProps) {
  const c = structure.community;
  const [inviteOpen, setInviteOpen] = useState(false);
  const invites = useQuery({
    queryKey: ["invites", c.id],
    queryFn: () =>
      api.get<{ items: { code: string; inviter: UserSummary | null; uses: number; maxUses: number; expiresAt: string | null; expired: boolean }[] }>(`/api/communities/${c.id}/invites`),
  });
  const revoke = async (code: string) => {
    try {
      await api.del(`/api/invites/${code}`);
      void invites.refetch();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const items = invites.data?.items ?? [];
  return (
    <div>
      <SettingsGroup
        title="Invite links"
        action={
          <Button size="sm" icon={IconUserPlus} onClick={() => setInviteOpen(true)}>
            New invite
          </Button>
        }
      >
        {items.map((i) => (
          <div key={i.code} className={cn("flex items-center gap-3 px-4 py-2.5", i.expired && "opacity-50")}>
            <span className="font-mono text-sm">{i.code}</span>
            <div className="min-w-0 flex-1 truncate text-xs text-fg-3 tabular">
              {i.inviter ? `@${i.inviter.username} · ` : ""}
              {i.uses}
              {i.maxUses ? `/${i.maxUses}` : ""} uses · {i.expired ? "expired" : i.expiresAt ? `expires ${new Date(i.expiresAt).toLocaleDateString("en-GB")}` : "never expires"}
            </div>
            <IconButton
              size="sm"
              label="Copy link"
              icon={IconCopy}
              onClick={() => {
                void copyText(`${window.location.origin}/invite/${i.code}`);
                toast.show("Link copied");
              }}
            />
            <IconButton size="sm" label="Revoke invite" icon={IconTrash} tone="danger" onClick={() => void revoke(i.code)} />
          </div>
        ))}
        {!invites.isPending && items.length === 0 && <p className="px-4 py-3 text-sm text-fg-3">No invite links yet.</p>}
        {invites.isPending && <RowSkeleton />}
      </SettingsGroup>
      <InviteDialog
        open={inviteOpen}
        onOpenChange={(o) => {
          setInviteOpen(o);
          if (!o) void invites.refetch();
        }}
        community={c}
      />
    </div>
  );
}

/* ───────────── Bans ───────────── */

export function BansSection({ structure }: SectionProps) {
  const c = structure.community;
  const [q, setQ] = useState("");
  const bans = useQuery({
    queryKey: ["bans", c.id],
    queryFn: () => api.get<{ items: { user: UserSummary; reason: string; createdAt: string }[] }>(`/api/communities/${c.id}/bans`),
  });
  const unban = async (userId: string) => {
    try {
      await api.del(`/api/communities/${c.id}/bans/${userId}`);
      void bans.refetch();
      toast.show("Unbanned");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const items = (bans.data?.items ?? []).filter((b) => !q || `${b.user.displayName} ${b.user.username}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bans" aria-label="Search bans" className="mb-4 !h-9" />
      {bans.isPending ? (
        <RowSkeleton />
      ) : items.length ? (
        <SettingsGroup>
          {items.map((b) => (
            <div key={b.user.id} className="flex items-center gap-3 px-4 py-2.5">
              <Avatar src={b.user.avatarUrl} name={b.user.displayName} size="md" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{b.user.displayName}</div>
                <div className="truncate text-xs text-fg-3">
                  @{b.user.username} · banned {timeAgo(b.createdAt)}
                  {b.reason && ` · ${b.reason}`}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => void unban(b.user.id)}>
                Unban
              </Button>
            </div>
          ))}
        </SettingsGroup>
      ) : (
        <EmptyState title="No bans" description="People you ban appear here." className="!py-10" />
      )}
    </div>
  );
}

/* ───────────── Audit log ───────────── */

const ACTION_LABELS: Record<AuditEntry["action"], string> = {
  "message.bulk_delete": "bulk deleted messages in",
  "member.nickname": "changed the nickname of",
  "webhook.create": "created webhook",
  "webhook.update": "updated webhook",
  "webhook.delete": "deleted webhook",
  "app.install": "added the app",
  "app.update": "changed the permissions of the app",
  "app.remove": "removed the app",
  "emoji.create": "added emoji",
  "emoji.update": "renamed emoji",
  "emoji.delete": "removed emoji",
  "sticker.create": "added sticker",
  "sticker.delete": "removed sticker",
  "event.create": "scheduled event",
  "event.update": "updated event",
  "event.delete": "cancelled event",
  "automod.block": "AutoMod blocked a message from",
  "report.resolve": "resolved a report about",
  "badge.award": "awarded a badge to",
  "badge.revoke": "removed a badge from",
  "staff.join": "joined as Atrium staff",
  "staff.warning": "issued an Atrium warning to",
  "staff.limit": "limited",
  "staff.suspend": "suspended",
  "staff.lift": "lifted Atrium enforcement on",
  "community.update": "updated the community",
  "community.transfer": "transferred ownership to",
  "role.create": "created role",
  "role.update": "updated role",
  "role.delete": "deleted role",
  "role.reorder": "reordered roles",
  "member.roles": "changed roles for",
  "member.kick": "kicked",
  "member.ban": "banned",
  "member.unban": "unbanned",
  "member.timeout": "timed out",
  "voice.mute": "changed voice mute for",
  "voice.disconnect": "disconnected from voice",
  "channel.create": "created channel",
  "channel.update": "updated channel",
  "thread.update": "updated post",
  "thread.delete": "deleted post",
  "channel.delete": "deleted channel",
  "category.create": "created category",
  "category.update": "renamed category",
  "category.delete": "deleted category",
  "invite.create": "created invite",
  "invite.delete": "revoked invite",
  "message.delete": "deleted a message by",
  "message.pin": "changed a pin in",
};

const FILTERS = [
  { value: "", label: "All actions" },
  { value: "member.", label: "Members" },
  { value: "role.", label: "Roles" },
  { value: "channel.", label: "Channels" },
  { value: "category.", label: "Categories" },
  { value: "invite.", label: "Invites" },
  { value: "message.", label: "Messages" },
  { value: "voice.", label: "Voice" },
  { value: "community.", label: "Community" },
  { value: "app.", label: "Apps" },
];

function humanKey(k: string) {
  return k.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === "") return "none";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "none";
  if (typeof v === "boolean") return v ? "on" : "off";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function Details({ e }: { e: AuditEntry }) {
  const d = e.details ?? {};
  const lines: string[] = [];
  if (e.action === "member.timeout") lines.push(Number(d.minutes) > 0 ? `For ${Number(d.minutes) >= 1440 ? `${Number(d.minutes) / 1440} days` : Number(d.minutes) >= 60 ? `${Number(d.minutes) / 60} hours` : `${d.minutes} minutes`}` : "Time-out removed");
  else if (e.action === "member.roles") {
    if ((d.added as string[])?.length) lines.push(`Added ${(d.added as string[]).join(", ")}`);
    if ((d.removed as string[])?.length) lines.push(`Removed ${(d.removed as string[]).join(", ")}`);
  } else if (e.action === "role.reorder") lines.push((d.order as string[] | undefined)?.join(" › ") ?? "");
  else if (e.action === "app.install" || e.action === "app.update") {
    const perms = permissionsIn(Number(d.permissions) || 0);
    lines.push(perms.length ? `Permissions: ${perms.map((p) => p.label).join(", ")}` : "No extra permissions");
  } else if (e.action === "app.remove") {
    /* Nothing more to say. */
  }
  else
    for (const [k, v] of Object.entries(d)) {
      if (k === "permissions" && typeof v === "object" && v && "to" in v) lines.push("Permissions changed");
      else if (k === "permissions" && typeof v === "number") lines.push(`Permissions: ${permissionsIn(v).map((p) => p.label).join(", ") || "none"}`);
      else if (v && typeof v === "object" && "to" in (v as object)) lines.push(`${humanKey(k)}: ${fmt((v as { from: unknown }).from)} → ${fmt((v as { to: unknown }).to)}`);
      else lines.push(`${humanKey(k)}: ${fmt(v)}`);
    }
  if (d.reason) lines.push(`Reason: ${d.reason}`);
  const shown = lines.filter(Boolean);
  if (!shown.length) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-fg-3">
      {shown.slice(0, 6).map((l, i) => (
        <li key={i} className="break-words">
          {l}
        </li>
      ))}
    </ul>
  );
}

export function AuditLogSection({ structure }: SectionProps) {
  const c = structure.community;
  const [action, setAction] = useState("");
  const q = useInfiniteQuery({
    queryKey: ["audit", c.id, action],
    queryFn: ({ pageParam }) => api.get<Page<AuditEntry>>(`/api/communities/${c.id}/audit-log${qs({ cursor: pageParam as string | undefined, action: action || undefined })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  const sentinel = useInView<HTMLDivElement>(() => q.hasNextPage && !q.isFetchingNextPage && void q.fetchNextPage(), !!q.hasNextPage);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Select aria-label="Filter actions" value={action} onChange={setAction} options={FILTERS} className="[&_select]:h-9" />
      </div>
      {q.isPending ? (
        <RowSkeleton />
      ) : items.length === 0 ? (
        <EmptyState icon={IconHistory} title="Nothing logged yet" description="Changes to roles, channels, members and settings appear here." className="!py-10" />
      ) : (
        <SettingsGroup>
          {items.map((e) => (
            <div key={e.id} className="flex gap-3 px-4 py-3">
              <Avatar src={e.actor?.avatarUrl} name={e.actor?.displayName ?? "?"} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-fg-2">
                  <span className="font-semibold text-fg">{e.actor?.displayName ?? (e.action === "staff.lift" ? "Atrium" : "Someone")}</span>
                  {e.actor?.staff && <NameTags user={e.actor} className="ml-1 align-[-3px]" />} {ACTION_LABELS[e.action] ?? e.action}{" "}
                  {e.targetName && <span className="font-medium text-fg">{e.targetName}</span>}
                </div>
                <Details e={e} />
              </div>
              <Tooltip label={chatTimestamp(e.createdAt)}>
                <time dateTime={e.createdAt} className="shrink-0 text-xs text-fg-3 tabular">
                  {timeAgo(e.createdAt)}
                </time>
              </Tooltip>
            </div>
          ))}
        </SettingsGroup>
      )}
      <div ref={sentinel} className="flex h-10 items-center justify-center text-fg-3">
        {q.isFetchingNextPage && <Spinner className="size-4" />}
      </div>
    </div>
  );
}
